import { Link, useNavigate, useLocation } from "react-router-dom";
import { Flame, LogOut, User as UserIcon, Instagram } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const handleLogout = () => {
    logout();
    nav("/");
  };

  const isDashboard = loc.pathname.startsWith("/dashboard") || loc.pathname.startsWith("/module") || loc.pathname.startsWith("/lesson") || loc.pathname.startsWith("/quiz") || loc.pathname.startsWith("/final-exam") || loc.pathname.startsWith("/certificate") || loc.pathname.startsWith("/profile") || loc.pathname.startsWith("/s98-review") || loc.pathname.startsWith("/glossary") || loc.pathname.startsWith("/lab") || loc.pathname.startsWith("/tracks") || loc.pathname.startsWith("/track/") || loc.pathname.startsWith("/instructor");
  const isInstructor = user?.role === "instructor";

  return (
    <header
      className="sticky top-0 z-40 w-full border-b border-zinc-200 bg-white/85 backdrop-blur-xl"
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2.5 group" data-testid="nav-logo-link">
          <div className="w-8 h-8 bg-red-600 flex items-center justify-center">
            <Flame className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-[15px] font-bold tracking-tight text-zinc-950">APM Fire Alarm</div>
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500">Exam Prep</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm text-zinc-700">
          {!user && (
            <>
              <a href="/#curriculum" className="hover:text-red-600 transition-colors" data-testid="nav-curriculum">Curriculum</a>
              <a href="/#features" className="hover:text-red-600 transition-colors" data-testid="nav-features">Features</a>
              <a href="/#nicet" className="hover:text-red-600 transition-colors" data-testid="nav-nicet">NICET Info</a>
              <Link to="/free-seminar" className="hover:text-red-600 transition-colors font-semibold" data-testid="nav-free-seminar">Free Seminar</Link>
            </>
          )}
          {user && isDashboard && !isInstructor && (
            <>
              <Link to="/dashboard" className="hover:text-red-600 transition-colors" data-testid="nav-dashboard">Dashboard</Link>
              <Link to="/tracks" className="hover:text-red-600 transition-colors" data-testid="nav-tracks">Tracks</Link>
              <Link to="/lab" className="hover:text-red-600 transition-colors" data-testid="nav-lab">Lab</Link>
              <Link to="/s98-review" className="hover:text-red-600 transition-colors" data-testid="nav-s98">NYC S-98</Link>
              <Link to="/glossary" className="hover:text-red-600 transition-colors" data-testid="nav-glossary">Glossary</Link>
              <Link to="/final-exam" className="hover:text-red-600 transition-colors" data-testid="nav-final-exam">Final Exam</Link>
              <Link to="/profile" className="hover:text-red-600 transition-colors" data-testid="nav-profile">Profile</Link>
            </>
          )}
          {user && isInstructor && (
            <>
              <Link to="/instructor/launch" className="hover:text-red-600 transition-colors font-semibold" data-testid="nav-launch">Launch</Link>
              <Link to="/instructor" className="hover:text-red-600 transition-colors" data-testid="nav-instructor">Cohort</Link>
              <Link to="/dashboard" className="hover:text-red-600 transition-colors" data-testid="nav-dashboard">Curriculum</Link>
              <Link to="/profile" className="hover:text-red-600 transition-colors" data-testid="nav-profile">Profile</Link>
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="https://www.instagram.com/apmfireexam_prep"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex text-zinc-500 hover:text-red-600 transition-colors"
            aria-label="Follow APM Fire Alarm Exam Prep on Instagram"
            data-testid="nav-instagram"
          >
            <Instagram className="w-4 h-4" />
          </a>
          {user ? (
            <>
              <Link to="/profile" className="hidden sm:flex items-center gap-2 text-sm group" data-testid="nav-user-chip">
                <div className="w-7 h-7 rounded-full bg-zinc-950 text-white grid place-items-center text-[11px] font-semibold group-hover:bg-red-600 transition-colors">
                  {user.full_name?.[0]?.toUpperCase() || <UserIcon className="w-3 h-3" />}
                </div>
                <span className="text-zinc-800 font-medium group-hover:text-red-600 transition-colors" data-testid="nav-user-name">{user.full_name}</span>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="rounded-sm"
                data-testid="nav-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-1" /> Log out
              </Button>
            </>
          ) : (
            <>
              <Link
                to="/free-seminar"
                className="md:hidden text-[10px] font-mono uppercase tracking-widest px-2.5 py-1.5 border border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-colors"
                data-testid="nav-free-seminar-mobile"
              >
                Free Seminar
              </Link>
              <Link to="/login" className="text-sm font-medium text-zinc-800 hover:text-red-600 transition-colors" data-testid="nav-login-link">
                Log in
              </Link>
              <Link to="/register" data-testid="nav-register-link">
                <button className="btn-primary text-sm">Enroll now</button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
